import {
  Box,
  Drawer,
  Typography,
  IconButton,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import React from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';


import type { MenuItem, MenuSection } from './menuData';
import { useMainSidebarSections } from './menuData';
import { setSidebarState } from '../hooks/useSidebarState';
import { getPathWithBasename } from '@/__core/helpers/getBasename';
import { getSelectedCompany } from '@/modules/auth/helpers/CompanyStorage';

// Constants
const DRAWER_WIDTH = 220;
const PRIMARY_COLOR = '#1e3a8a';

/**
 * Match menu path to current route. Prefix match is allowed, but a shorter sibling
 * path must not win when a longer one also matches (e.g. /timekeepings vs /timekeepings/detail).
 */
const pathMatchesMenuItem = (
  itemPath: string,
  currentPath: string,
  siblingPaths?: string[]
): boolean => {
  if (!itemPath) return false;
  if (currentPath === itemPath) return true;
  if (itemPath === '/' || !currentPath.startsWith(`${itemPath}/`)) {
    return false;
  }

  if (siblingPaths?.length) {
    const hasMoreSpecificMatch = siblingPaths.some(
      (other) =>
        other !== itemPath &&
        other.length > itemPath.length &&
        (currentPath === other || currentPath.startsWith(`${other}/`))
    );
    if (hasMoreSpecificMatch) {
      return false;
    }
  }

  return true;
};

const getSectionItemPaths = (section: MenuSection): string[] =>
  section.items
    .filter((item): item is MenuItem & { path: string } => 'path' in item && Boolean(item.path))
    .map((item) => item.path);

interface SidebarLeftProps {
  open: boolean;
  onToggle: (open: boolean) => void;
}

// Styles
const drawerStyles = (open: boolean) => ({
  width: open ? DRAWER_WIDTH : 0,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    backgroundColor: PRIMARY_COLOR,
    color: 'white',
    transform: open ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.3s ease',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '3px',
    },
  
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1)',
  },
});

const sectionTitleStyles = {
  px: 0,
  py: 0,
  display: 'block',
  color: 'white',
  fontSize: '0.9rem',
};

const listItemButtonStyles = (isActive: boolean) => ({
  px: 3,
  pr: 0,
  margin: '0px',
  minHeight: '30px',
  backgroundColor: 'transparent',
  '&:hover': {
    textDecoration: 'underline',
    textDecorationColor: 'white',
  },
});

const listItemTextStyles = (isActive: boolean) => ({
  fontSize: '0.875rem',
  fontWeight: isActive ? '700' : '400',
  color: isActive ? 'white' : 'rgba(255, 255, 255, 0.85)',
  letterSpacing: '0.1px',
  transition: 'all 0.2s ease',
  textDecoration: isActive ? 'underline' : 'none',
  textDecorationColor: 'white',
  '&:hover': {
    textDecoration: 'underline',
    textDecorationColor: 'white',
  },
});

// Components
const SidebarHeader = ({ onToggle, companyCode }: {
  onToggle: (open: boolean) => void; 
  companyCode: string;
}) => {
  return (
    <Box sx={{ p: 1, pt: 2, pl: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ 
          fontWeight: '700',
          fontSize: '2rem',
          letterSpacing: '0.5px',
          background: 'linear-gradient(45deg, #ffffff, #e3f2fd)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          {companyCode}
        </Typography>
        <IconButton
          onClick={() => {
            onToggle(false);
            setSidebarState(false);
          }}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

const SidebarItem = ({ 
  item, 
  isActive, 
  onClick,
  showIcon = true,
  itemSx = {},
  iconContainerSx = {},
}: { 
  item: MenuItem; 
  isActive: boolean; 
  onClick: () => void;
  showIcon?: boolean;
  itemSx?: object;
  iconContainerSx?: object;
}) => (
  <Box
    component="li"
    sx={{
      listStyle: 'none',
      margin: 0,
      padding: 0,
    }}
  >
    <Box
      component="a"
      href={('path' in item && item.path) ? item.path : '#'}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      sx={{
        ...listItemButtonStyles(isActive),
        pl: showIcon ? 3 : 0,
        ...itemSx,
        width: '100%',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {showIcon && (
        <Box sx={{ 
          color: isActive ? 'white' : 'rgba(255, 255, 255, 0.8)', 
          minWidth: 40,
          opacity: isActive ? 1 : 0.8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...iconContainerSx,
        }}>
          {item.icon}
        </Box>
      )}
      <Box sx={listItemTextStyles(isActive)}>
        {item.text}
      </Box>
    </Box>
  </Box>
);

const SidebarSection = ({ 
  section, 
  currentPath, 
  onItemClick,
  allSections,
  isExpanded,
  onToggle,
}: { 
  section: MenuSection; 
  currentPath: string; 
  onItemClick: (path: string) => void;
  allSections: MenuSection[];
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const sectionItemPaths = getSectionItemPaths(section);

  const hasActiveItem = section.items.some((item) => {
    if ('path' in item && item.path) {
      return pathMatchesMenuItem(item.path, currentPath, sectionItemPaths);
    }
    return false;
  });

  const getAppName = (path: string, allSections: MenuSection[]) => {
    for (const menuSection of allSections) {
      const pathsInSection = getSectionItemPaths(menuSection);
      const matchingItem = menuSection.items.find((item) => {
        if (item.path) {
          return pathMatchesMenuItem(item.path, path, pathsInSection);
        }
        return false;
      });

      if (matchingItem && menuSection.title) {
        return menuSection.title;
      }
    }

    return section.title;
  };

  const currentAppName = getAppName(currentPath, allSections);
  const shouldShowAppName = hasActiveItem;
  
  return (
    <Box
      sx={{
        ml: 0,
        mr: 1.5,
        mt: 0.75,
        mb: 0.25,
        pt: 0.75,
        pb: 0.25,
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
      }}
    >
      {section.showSection === false ? (
        <Box component="ul" sx={{ py: 0, pl: 0, m: 0, listStyle: 'none', '& > li': { margin: 0, padding: 0 } }}>
          {section.items.map((item) => {
            const isActive = 'path' in item && item.path && currentPath === item.path;
            return (
              <SidebarItem
                key={item.id}
                item={item}
                isActive={Boolean(isActive)}
                showIcon={true}
                itemSx={{
                  pl: 1,
                  py: 0.375,
                  minHeight: 'auto',
                }}
                iconContainerSx={{
                  minWidth: 12,
                  width: 12,
                  height: 12,
                  mr: 1.5,
                  justifyContent: 'flex-start',
                  '& .MuiSvgIcon-root': {
                    fontSize: '16px',
                  },
                }}
                onClick={() => { if ('path' in item && item.path) onItemClick(item.path); }}
              />
            );
          })}
        </Box>
      ) : (
      <>
      {section.title && (
        <Box
          component="button"
          type="button"
          onClick={onToggle}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            width: '100%',
            pl: 1,
            pr: 0,
            py: 0.375,
            border: 'none',
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
            textAlign: 'left',
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: 12,
            height: 12,
            mr: 1.5,
            fontWeight: '700',
            color: hasActiveItem ? 'white' : 'rgba(255, 255, 255, 0.8)',
            '& .MuiSvgIcon-root': {
              fontSize: '16px',
            },
          }}>
            {section.items[0]?.icon}
          </Box>
          <Typography 
            variant="caption" 
            sx={{
              ...sectionTitleStyles,
              color: hasActiveItem ? 'white' : 'rgba(255, 255, 255, 0.7)',
              fontWeight: hasActiveItem ? '900' : '700',
              margin: 0,
              padding: 0,
            }}
          >
            {shouldShowAppName ? currentAppName : section.title}
          </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: hasActiveItem ? 'white' : 'rgba(255, 255, 255, 0.8)',
              '& .MuiSvgIcon-root': {
                fontSize: '18px',
              },
            }}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Box>
        </Box>
      )}
      {isExpanded && (
        <Box
          component="ul"
          sx={{
            py: 0.5,
            pl: '32px',
            pr: 0,
            mt: 0.5,
            margin: 0,
            listStyle: 'none',
            '& > li': {
              margin: 0,
              padding: 0,
            },
          }}
        >
          {section.items.map((item) => {
            const isActive =
              'path' in item &&
              item.path &&
              pathMatchesMenuItem(item.path, currentPath, sectionItemPaths);

            return (
              <SidebarItem
                key={item.id}
                item={item}
                isActive={Boolean(isActive)}
                showIcon={false}
                onClick={() => {
                  if ('path' in item && item.path) {
                    onItemClick(item.path);
                  }
                }}
              />
            );
          })}
        </Box>
      )}
      </>
      )}
    </Box>
  );
};

// Main Component
export default function SidebarLeft({ open, onToggle }: SidebarLeftProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const loginCompany = getSelectedCompany();
  const companyCode = (loginCompany?.company_code || 'XBOX').toUpperCase();
  const mainSections = useMainSidebarSections();
  const sectionIds = React.useMemo(
    () => mainSections.map((section) => section.id),
    [mainSections]
  );
  const sectionIdsKey = sectionIds.join('|');

  const [expandedSections, setExpandedSections] = React.useState<Record<string, boolean>>(() =>
    Object.fromEntries(sectionIds.map((sectionId) => [sectionId, false]))
  );

  // Keep expanded state when permission filter changes; only init keys that are new.
  React.useEffect(() => {
    setExpandedSections((previous) => {
      let hasChanges = false;
      const nextState: Record<string, boolean> = { ...previous };

      sectionIds.forEach((sectionId) => {
        if (!(sectionId in nextState)) {
          nextState[sectionId] = false;
          hasChanges = true;
        }
      });

      Object.keys(nextState).forEach((key) => {
        if (!sectionIds.includes(key)) {
          delete nextState[key];
          hasChanges = true;
        }
      });

      return hasChanges ? nextState : previous;
    });
  }, [sectionIdsKey]);

  // Auto-expand the section that contains the current route when navigating.
  // Do not depend on mainSections — useMainSidebarSections returns a new array each render,
  // which would re-run this effect and undo manual collapse (e.g. section "Kho").
  React.useEffect(() => {
    setExpandedSections((previous) => {
      let hasChanges = false;
      const nextState = { ...previous };

      mainSections.forEach((section) => {
        if (section.showSection === false) {
          return;
        }
        const sectionItemPaths = getSectionItemPaths(section);
        const hasActiveItem = section.items.some(
          (item) =>
            'path' in item &&
            item.path &&
            pathMatchesMenuItem(item.path, location.pathname, sectionItemPaths)
        );
        if (hasActiveItem && !nextState[section.id]) {
          nextState[section.id] = true;
          hasChanges = true;
        }
      });

      return hasChanges ? nextState : previous;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- expand only on route change
  }, [location.pathname]);

  const handleItemClick = (path: string) => {
    if (path === location.pathname) {
      return;
    }

    const isStuckSettingsPage =
      location.pathname === '/settings' &&
      new URLSearchParams(location.search).get('setting') === 'setting-financial-type';

    if (isStuckSettingsPage) {
      window.location.assign(getPathWithBasename(path));
      return;
    }

    flushSync(() => {
      navigate(path);
    });
  };

  const handleToggleSection = (sectionId: string) => {
    setExpandedSections((previous) => ({
      ...previous,
      [sectionId]: !previous[sectionId],
    }));
  };

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={drawerStyles(open)}
    >
      <SidebarHeader 
        onToggle={onToggle}
        companyCode={companyCode}
      />

      <Box sx={{ flexGrow: 1, px: 0.5, pb: 2 }}>
        {/* Navigation Section - Always show */}
        

        {mainSections.map((section) => (
          <Box key={section.id}>
            <SidebarSection
              section={section}
              currentPath={location.pathname}
              onItemClick={handleItemClick}
              allSections={mainSections}
              isExpanded={Boolean(expandedSections[section.id])}
              onToggle={() => handleToggleSection(section.id)}
            />
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}